import type { Balances } from '@polaris/core/models'
import { hasSignificantBalanceChanges } from '@polaris/core/utils'
import { queryOptions } from '@tanstack/react-query'

export const balancesStreamQuery = (address?: string) =>
  queryOptions<Balances[]>({
    queryKey: address
      ? ['balances', 'stream', address]
      : ['balances', 'stream'],
    enabled: Boolean(address),
    /**
     * Custom structural sharing function for React Query.
     *
     * Uses the order-independent hasSignificantBalanceChanges utility to determine
     * if there are meaningful changes in the balance data. If no significant changes
     * are detected, returns the old data reference to prevent unnecessary re-renders.
     *
     * This improves performance by:
     * - Preventing unnecessary state updates for insignificant price fluctuations
     * - Using order-independent comparison (works regardless of data ordering)
     * - Reusing the exact same reference when no meaningful changes occur
     * - Only updating when changes exceed the $0.01 threshold or structural changes happen
     *
     * See: https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations#structural-sharing
     *
     * @returns Old data reference if no significant changes, otherwise new data
     */
    structuralSharing: (_oldData, _newData) => {
      const oldData = _oldData as Balances[]
      const newData = _newData as Balances[]

      // Special case: If old data is undefined (first load), always return new data
      // to establish initial query state, even if new data is null/empty
      // This is required because React Query won't call select() if data stays undefined
      if (oldData === undefined || oldData === null) {
        return newData
      }

      // If no significant changes detected, return the old reference to prevent updates
      if (!hasSignificantBalanceChanges(oldData, newData)) {
        return oldData
      }

      // Significant changes detected, return new data
      return newData
    },
  })
